"use client";

import useRouter from "@stadium/hooks/lib/use-custom-route";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { useWindowStorage } from "@hooks/use-window-storage";
import { useFlags } from "@stadium/services/launch-darkly/use-flags";
import {
  getIsBundleShop,
  getIsHideBulkPricing,
  getIsLockerShop,
  getIsPricelessShop,
  getIsSendToManyAllowed,
  getWorkspaceUuidFromStore,
  getStoreType,
} from "@selectors/store";

import { isMomentStore } from "@helpers/store";

import { getCartOrderFetching, getCartOrderId, getCartOrderSendToMany } from "@selectors/cart";

import { useCreateShopContext } from "@contexts/create-shop-context";
import { useWorkspaceContext } from "@contexts/workspace-context";
import { useHandleSendToManyToggle } from "@hooks/cart/use-handle-send-to-many-toggle";

import { SEND_ITEMS, SEND_TO_MYSELF, SEND_TO_OTHERS } from "@constants/navigation";
import { workspaceLaunchFlag } from "@stadium/services/launch-darkly/constants";

const defaultObj = {};

export const SendItemsContext = React.createContext(defaultObj);

export const useSendItemsContext = () => useContext(SendItemsContext);

export const SendItemsProvider = ({ children }) => {
  const { query } = useRouter();
  const sendItems = query[SEND_ITEMS];

  const { [workspaceLaunchFlag]: workspaceLaunchFlagEnabled } = useFlags();

  const hideBulkPricing = useSelector(getIsHideBulkPricing);
  const orderId = useSelector(getCartOrderId);
  const isCartOrderFetching = useSelector(getCartOrderFetching);
  const cartOrderSendToManyStatus = useSelector(getCartOrderSendToMany);
  const isSendToManyAllowed = useSelector(getIsSendToManyAllowed);
  const isLockerShop = useSelector(getIsLockerShop);
  const workspaceUuidFromStore = useSelector(getWorkspaceUuidFromStore);
  const isPricelessShop = useSelector(getIsPricelessShop);
  const isKitShop = useSelector(getIsBundleShop);
  const { permissions, workspaceData } = useWorkspaceContext();
  const storeType = useSelector(getStoreType);
  const { uuid: workspaceUuid } = workspaceData?.workspace || defaultObj;
  const { isNewShopCreationFlow } = useCreateShopContext();

  const { updateOrderStatus } = useHandleSendToManyToggle();

  const [sendItemsSelection, setSendItemsSelection] = useWindowStorage(
    SEND_ITEMS,
    SEND_TO_MYSELF,
    "sessionStorage",
  );

  const isPublicTempalteShop = isMomentStore(storeType);

  const isSendToMyself = sendItemsSelection === SEND_TO_MYSELF;

  const [isCountrySelectorModalOpen, setIsCountrySelectorModalOpen] = useState(false);

  const openCountrySelectorModal = () => setIsCountrySelectorModalOpen(true);
  const closeCountrySelectorModal = () => setIsCountrySelectorModalOpen(false);
  const showSendToManyOption = useMemo(
    () =>
      !isCartOrderFetching &&
      !isLockerShop &&
      isSendToManyAllowed &&
      !isNewShopCreationFlow &&
      !isPricelessShop &&
      !isKitShop &&
      permissions.canSendItems &&
      (!workspaceLaunchFlagEnabled ||
        workspaceUuidFromStore === workspaceUuid ||
        isPublicTempalteShop),
    [
      isCartOrderFetching,
      isLockerShop,
      isSendToManyAllowed,
      isNewShopCreationFlow,
      isPricelessShop,
      permissions.canSendItems,
      workspaceLaunchFlagEnabled,
      workspaceUuidFromStore,
      workspaceUuid,
      isPublicTempalteShop,
    ],
  );

  useEffect(() => {
    if (isCartOrderFetching) return;
    if (!showSendToManyOption && !isSendToMyself) {
      setSendItemsSelection(SEND_TO_MYSELF);
      updateOrderStatus(SEND_TO_MYSELF);
      return;
    }
    if (orderId) {
      const sendItemsValue = cartOrderSendToManyStatus ? SEND_TO_OTHERS : SEND_TO_MYSELF;
      setSendItemsSelection(sendItemsValue);
      return;
    }
    if (sendItems === "true") {
      setSendItemsSelection(SEND_TO_OTHERS);
    }
  }, [isCartOrderFetching, sendItems, cartOrderSendToManyStatus, orderId, showSendToManyOption]);

  const value = useMemo(
    () => ({
      sendItemsSelection,
      setSendItemsSelection,
      isSendToMyself,
      showSendToManyOption,
      isCountrySelectorModalOpen,
      openCountrySelectorModal,
      closeCountrySelectorModal,
      isSendToOthers: !isSendToMyself,
      volumeDiscountProductsVariables: {
        excludeTierPricing: hideBulkPricing,
      },
    }),
    [
      sendItemsSelection,
      isSendToMyself,
      showSendToManyOption,
      isCountrySelectorModalOpen,
      hideBulkPricing,
    ],
  );
  return <SendItemsContext.Provider value={value}>{children}</SendItemsContext.Provider>;
};
